/**
 * The composer / SEBS-frontier-ux experiment, resolved at the global (carvana-layout)
 * layer for SRP + VDP and in Home's getServerSideProps — deliberately outside the
 * per-page experiment aggregation so it only buckets on the pages that opt in via
 * isComposerExperimentPage.
 */
export const SEBS_COMPOSER_EXPERIMENT = 'car-merch-sebs-frontier-ux';

/**
 * Registers SEBS_COMPOSER_EXPERIMENT with the shared experiments list purely so the
 * proxy middleware's setExperimentCookies mirrors a ?car-merch-sebs-frontier-ux=<bucket>
 * query param into a cookie for QA/dev (see src/proxy-utils/setExperimentCookies.ts).
 * This does NOT re-enable the per-page bucketing aggregation described above — actual
 * bucketing still only happens in getShouldShowComposerTreatment.ts.
 */
export const clientSideBrowserCookieIdExperiments: Record<string, never> = {};

export const serverSideBrowserCookieIdExperiments = {
  [SEBS_COMPOSER_EXPERIMENT]: {}
};

export const clientSideUserIdExperiments: Record<string, never> = {};

export const serverSideUserIdExperiments: Record<string, never> = {};

/**
 * Cookie or query param name that forces the composer experience on for QA/dev,
 * even when the real experiment bucket/device checks would otherwise say no.
 */
export const COMPOSER_ENABLED_OVERRIDE_PARAM = 'composer_enabled';

/**
 * Query param appended to a composer-driven redirect/navigation to SRP so the
 * destination page knows to reopen the composer chat once it's ready, rather than
 * silently applying the filters. Consumed once and stripped from the URL.
 */
export const COMPOSER_OPEN_PARAM = 'composer_open';

/**
 * Closed set of reasons revertToControl can be called with, so a typo can't silently
 * corrupt the DataDog dimension used to diagnose why the safety mechanism fired.
 */
export type ComposerRevertReason =
  | 'component-error'
  | 'component-timeout'
  | 'ready-timeout';

/**
 * The experiment lives on SRP (/cars) and the standard VDP (/vehicle/<id>) only.
 * VDP is matched by shape (a numeric vehicle ID, per getVehicleId.ts's parseInt-based
 * parsing) rather than by excluding known non-ID routes, so a future /vehicle/* route
 * doesn't silently become SEBS-eligible unless it's actually a numeric VDP URL.
 */
const VDP_PATHNAME_REGEX = /^\/vehicle\/\d+\/?$/;

export const isComposerExperimentPage = (pathname: string): boolean => {
  return pathname.startsWith('/cars') || VDP_PATHNAME_REGEX.test(pathname);
};
